/**
 * @description Edit modal for an active/paused recurring plan.
 *
 * Phase 1 (migration 179) — start_date is the canonical billing cycle anchor.
 * Day-of-month / day-of-week are no longer user-editable; the backend derives
 * them from start_date. Editing the anchor is a deliberate "shift billing
 * cycle" action that requires a confirmation with a reason (audit-logged in
 * the recurring_plan_cycle_changes table).
 *
 * Key features:
 * - Pre-filled with current plan values.
 * - Anchor (start_date) editable; other schedule changes (frequency, etc.)
 *   require cancel-and-recreate.
 * - Cycle-shift confirmation: shows old vs. new next charge date, takes a
 *   required reason, audit-logged, auto-moves linked future events.
 * - Live schedule preview via /recurring-plans/preview. In-flight preview
 *   results are dropped on cleanup so a slower older response cannot show a
 *   stale next-charge date on cycle-shift confirm.
 *
 * @dependencies
 * - recurringPlanService: API client for plan update + preview
 * - recurringPlanForm.startSchedulePreview: debounce + ignore cancelled fetches
 * - storedCardsService: Load customer's cards
 * - discountService: re-evaluate context=recurring_plan when products change
 * - planProductLinesChanged: omit `items` and skip live evaluate until product identity changes
 */

import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import recurringPlanService from '../../services/api/recurringPlanService';
import discountService from '../../services/api/discountService';
import { getApiErrorMessages } from '../../utils/apiErrorMessage';
import storedCardsService from '../../services/api/storedCardsService';
import RecurringPlanProductsField from './RecurringPlanProductsField';
import formatStackedDiscounts from '../../utils/formatStackedDiscounts';
import planProductLinesChanged from '../../utils/planProductLinesChanged';
import { startSchedulePreview, localTodayYmd } from '../../utils/recurringPlanForm';

const FREQUENCY_LABELS = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly'
};

// Render YYYY-MM-DD as a friendly local date without UTC-midnight TZ shifting.
function formatChargeDate(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = String(isoDate).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
  });
}

export default function EditRecurringPlanModal({ plan, onClose, onUpdated }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);

  // Editable fields — pre-filled from plan
  const [amountDollars, setAmountDollars] = useState(
    plan.amount_cents ? (plan.amount_cents / 100).toFixed(2) : ''
  );
  const [description, setDescription] = useState(plan.description || '');
  const [productItems, setProductItems] = useState(() => (plan.items || []).map((item) => ({
    productId: item.product_id || item.productId,
    variantId: item.variant_id || item.variantId || null,
    quantity: Number(item.quantity) || 1,
    itemName: item.item_name || item.itemName,
    unitPriceCents: Number(item.unit_price_cents != null ? item.unit_price_cents : item.unitPriceCents) || 0
  })));
  const [appliedDiscounts, setAppliedDiscounts] = useState(plan.appliedDiscounts || []);
  const [evaluatedTotalCents, setEvaluatedTotalCents] = useState(
    plan.items && plan.items.length > 0 ? Number(plan.amount_cents) : null
  );
  const [storedCardId, setStoredCardId] = useState(plan.stored_card_id || '');
  const [endDate, setEndDate] = useState(plan.end_date ? plan.end_date.slice(0, 10) : '');
  const [maxOccurrences, setMaxOccurrences] = useState(plan.max_occurrences || '');
  const [hasEndCondition, setHasEndCondition] = useState(!!(plan.end_date || plan.max_occurrences));
  const [endConditionType, setEndConditionType] = useState(plan.end_date ? 'date' : 'occurrences');

  // Billing cycle anchor (replaces day-of-month / day-of-week pickers)
  const originalStartDate = plan.start_date ? plan.start_date.slice(0, 10) : '';
  const [startDate, setStartDate] = useState(originalStartDate);
  const anchorChanged = startDate && startDate !== originalStartDate;

  // Cycle-shift confirmation
  const [showShiftConfirm, setShowShiftConfirm] = useState(false);
  const [cycleShiftReason, setCycleShiftReason] = useState('');

  // Live schedule preview
  const [schedulePreview, setSchedulePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    const loadCards = async () => {
      try {
        setCardsLoading(true);
        const result = await storedCardsService.getCustomerCards(plan.customer_id);
        setCards((result.cards || []).filter(c => c.isActive !== false));
      } catch (err) {
        console.error('Failed to load cards:', err);
      } finally {
        setCardsLoading(false);
      }
    };
    loadCards();
  }, [plan.customer_id]);

  // Debounced preview when the anchor or end conditions change. Invalidate
  // immediately and drop in-flight results so cycle-shift confirm cannot show
  // a slower older firstChargeDate.
  useEffect(() => {
    if (!startDate) {
      setSchedulePreview(null);
      setPreviewLoading(false);
      return undefined;
    }
    setSchedulePreview(null);
    setPreviewLoading(true);
    return startSchedulePreview({
      delayMs: 250,
      fetchPreview: () => recurringPlanService.previewSchedule({
        startDate,
        frequency: plan.frequency,
        intervalCount: plan.interval_count,
        endDate: hasEndCondition && endConditionType === 'date' && endDate ? endDate : undefined,
        maxOccurrences: hasEndCondition && endConditionType === 'occurrences' && maxOccurrences
          ? parseInt(maxOccurrences, 10) : undefined,
        viewerToday: localTodayYmd(),
      }),
      onResult: (result) => setSchedulePreview(result),
      onError: (err) => {
        console.error('Failed to fetch schedule preview', err);
        setSchedulePreview(null);
      },
      onSettled: () => setPreviewLoading(false),
    });
  }, [startDate, hasEndCondition, endConditionType, endDate, maxOccurrences, plan.frequency, plan.interval_count]);

  useEffect(() => {
    if (productItems.length === 0) {
      setAppliedDiscounts([]);
      setEvaluatedTotalCents(null);
      return undefined;
    }
    if (!planProductLinesChanged(productItems, plan.items || [])) {
      setAppliedDiscounts(plan.appliedDiscounts || []);
      setEvaluatedTotalCents(Number(plan.amount_cents));
      return undefined;
    }
    const handle = setTimeout(async () => {
      const subtotalCents = productItems.reduce(
        (sum, item) => sum + (Number(item.unitPriceCents) || 0) * (Number(item.quantity) || 1),
        0
      );
      try {
        const result = await discountService.evaluate({
          context: 'recurring_plan',
          isRecurring: true,
          items: productItems.map((item) => ({
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity
          })),
          subtotalCents
        });
        setAppliedDiscounts(result.appliedDiscounts || []);
        setEvaluatedTotalCents(Number(result.totalCents));
      } catch (err) {
        console.error('Failed to evaluate plan discounts', err);
        setEvaluatedTotalCents(subtotalCents);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [productItems, plan.items, plan.appliedDiscounts, plan.amount_cents]);

  const hasProducts = productItems.length > 0;
  const displayAmountCents = hasProducts
    ? (evaluatedTotalCents != null
      ? evaluatedTotalCents
      : productItems.reduce(
        (sum, item) => sum + (Number(item.unitPriceCents) || 0) * (Number(item.quantity) || 1),
        0
      ))
    : Math.round((parseFloat(amountDollars) || 0) * 100);

  const performSave = async () => {
    const itemsChanged = planProductLinesChanged(productItems, plan.items || []);
    const amount = hasProducts ? displayAmountCents / 100 : parseFloat(amountDollars);
    // Card/schedule-only saves omit items and keep persisted amount_cents.
    // Do not block those on a live evaluate of 0.
    if (itemsChanged || !hasProducts) {
      if (isNaN(amount) || amount <= 0) {
        setError('Amount must be greater than zero');
        return;
      }
    }

    if (anchorChanged && !cycleShiftReason.trim()) {
      setError('A reason is required when changing the billing cycle.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const updates = {
        storedCardId: storedCardId || undefined,
        endDate: hasEndCondition && endConditionType === 'date' && endDate ? endDate : null,
        maxOccurrences: hasEndCondition && endConditionType === 'occurrences' && maxOccurrences
          ? parseInt(maxOccurrences, 10) : null,
      };

      if (hasProducts) {
        updates.description = null;
        if (itemsChanged) {
          updates.items = productItems.map((item) => ({
            productId: item.productId,
            variantId: item.variantId || undefined,
            quantity: item.quantity
          }));
        }
      } else {
        updates.amountCents = Math.round(amount * 100);
        updates.description = description || undefined;
        if (itemsChanged) {
          updates.items = [];
        }
      }

      if (anchorChanged) {
        updates.startDate = startDate;
        updates.viewerToday = localTodayYmd();
        updates.cycleShiftReason = cycleShiftReason.trim();
      }

      await recurringPlanService.updatePlan(plan.id, updates);
      onUpdated();
    } catch (err) {
      setError(getApiErrorMessages(err, 'Failed to update plan').join(' '));
    } finally {
      setSubmitting(false);
      setShowShiftConfirm(false);
    }
  };

  const handleSubmit = () => {
    if (anchorChanged) {
      setShowShiftConfirm(true);
    } else {
      performSave();
    }
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      {/* This modal renders inside the parent slide-over's <Dialog> JSX
          (see RecurringPlanDetail.js) so Headless UI's nested-Dialog stack
          tracks it correctly via React context — Esc/backdrop on this
          modal close THIS modal, not the slide-over. The z-[60] keeps it
          visually above the slide-over. The `submitting` guard below
          prevents Esc-during-save from unmounting the modal mid-fetch
          and dropping the response. */}
      <Dialog as="div" className="relative z-[60]" onClose={submitting ? () => {} : onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">Edit Recurring Plan</Dialog.Title>
            <p className="text-sm text-gray-500 mt-0.5">Most changes take effect on the next billing cycle</p>
          </div>
          <button onClick={onClose} className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Frequency (locked) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
            <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-600">
              {plan.interval_count > 1 ? `Every ${plan.interval_count} ` : ''}
              {FREQUENCY_LABELS[plan.frequency] || plan.frequency}
              <span className="text-xs text-gray-400 ml-2">(cannot be changed — cancel and recreate instead)</span>
            </div>
          </div>

          <RecurringPlanProductsField items={productItems} onChange={setProductItems} />

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={hasProducts ? (displayAmountCents / 100).toFixed(2) : amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              readOnly={hasProducts}
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand ${hasProducts ? 'bg-gray-50 text-gray-700' : ''}`}
            />
            {hasProducts && appliedDiscounts.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {formatStackedDiscounts(appliedDiscounts)}
              </p>
            )}
          </div>

          {!hasProducts && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          )}

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            {cardsLoading ? (
              <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
            ) : (
              <select
                value={storedCardId}
                onChange={(e) => setStoredCardId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              >
                {cards.map(card => (
                  <option key={card.id} value={card.id}>
                    {card.cardBrand || 'Card'} ****{card.lastFour} — expires {String(card.expiryMonth).padStart(2, '0')}/{String(card.expiryYear).slice(-2)}
                    {card.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Billing Cycle Anchor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Billing Cycle Anchor
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <p className="text-xs text-gray-500 mt-1">
              The customer is billed on this day of each cycle. Changing this is a "shift billing cycle" action and is audit-logged.
            </p>
            {anchorChanged && (
              <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900 text-xs">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  Anchor changing from <strong>{formatChargeDate(originalStartDate)}</strong> to <strong>{formatChargeDate(startDate)}</strong>.
                  Linked appointments will be moved to match.
                </div>
              </div>
            )}
            {(previewLoading || schedulePreview?.upcomingCharges?.length > 0) && (
              <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2">
                <p className="text-xs font-medium text-indigo-900 mb-1">
                  Upcoming charges
                  {previewLoading && <span className="ml-2 text-indigo-500">updating…</span>}
                </p>
                {schedulePreview?.upcomingCharges?.length > 0 && (
                  <ul className="text-xs text-indigo-900 space-y-0.5">
                    {schedulePreview.upcomingCharges.slice(0, 4).map((d) => (
                      <li key={d}>{formatChargeDate(d)}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* End Condition */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <input
                type="checkbox"
                checked={hasEndCondition}
                onChange={(e) => setHasEndCondition(e.target.checked)}
                className="rounded border-gray-300 text-brand focus:ring-brand"
              />
              Set an end condition
            </label>

            {hasEndCondition && (
              <div className="pl-6 space-y-3">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="editEndCondition"
                      checked={endConditionType === 'date'}
                      onChange={() => setEndConditionType('date')}
                      className="text-brand focus:ring-brand"
                    />
                    End date
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="editEndCondition"
                      checked={endConditionType === 'occurrences'}
                      onChange={() => setEndConditionType('occurrences')}
                      className="text-brand focus:ring-brand"
                    />
                    Number of payments
                  </label>
                </div>
                {endConditionType === 'date' ? (
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                ) : (
                  <input
                    type="number"
                    min="1"
                    max="999"
                    placeholder="e.g., 12"
                    value={maxOccurrences}
                    onChange={(e) => setMaxOccurrences(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Cycle-shift confirmation overlay */}
        {showShiftConfirm && (
          <div className="absolute inset-0 bg-white z-10 flex flex-col">
            <div className="px-6 py-4 border-b bg-amber-50">
              <h4 className="text-base font-semibold text-amber-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Shift Billing Cycle
              </h4>
              <p className="text-sm text-amber-800 mt-1">
                You're moving the billing cycle anchor. This is audit-logged.
              </p>
            </div>
            <div className="p-6 space-y-4 flex-1 overflow-y-auto">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Anchor</span>
                  <span className="font-medium">{formatChargeDate(originalStartDate)} → {formatChargeDate(startDate)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Next charge</span>
                  <span className="font-medium">
                    {formatChargeDate(plan.next_charge_date)} →{' '}
                    {schedulePreview?.firstChargeDate ? formatChargeDate(schedulePreview.firstChargeDate) : '—'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Any future appointments tied to this plan will be moved to the new anchor (time-of-day preserved).
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cycleShiftReason}
                  onChange={(e) => setCycleShiftReason(e.target.value)}
                  rows={3}
                  placeholder="e.g., Customer requested billing on the 15th instead of the 1st"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowShiftConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Back
              </button>
              <button
                onClick={performSave}
                disabled={submitting || previewLoading || !cycleShiftReason.trim()}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg font-medium text-sm hover:bg-amber-700 disabled:opacity-50"
              >
                {submitting ? 'Shifting...' : 'Confirm Shift'}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || previewLoading}
            className="px-6 py-2 bg-brand text-white rounded-lg font-medium text-sm hover:bg-brand-hover disabled:opacity-50"
          >
            {submitting ? 'Saving...' : (anchorChanged ? 'Review Cycle Shift' : 'Save Changes')}
          </button>
        </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
